import { type Theme } from '@emotion/react';
import { color } from './Styles';

export const emotionLightTheme: Theme = {
    mode: 'light',
    panelPosition: 'right',
    panelOrientation: 'vertical',
    color: {
        background: color.bg_light,
        card: color.neutral_accent_2,
        cardHover: color.secondary_light,
        accentCard: color.neutral_accent_2,
        border: color.secondary_light,
        text: color.primary_dark,
        textBody: color.neutral,
        error: color.error,
        neutral: color.border,
        link: color.secondary,
        accent: color.secondary,
        tint: color.accent,
        danger: color.tertiary_updated,
        success: color.success,
        secondary: color.secondary,
        sidebarDivider: color.secondary_light,
        indicator: color.secondary,
        waveVoice: color.accent_dark,
        waveDictation: color.tertiary,
    },
    input: {
        text: '#1A1C1E',
        placeholder: '#54606F',
        border: color.border,
        background: color.neutral_primary,
    },
    dialog: {
        background: color.bg_light,
        text: color.primary_intense,
        warn: color.accent_2,
        success: color.success_light,
        remote: color.accent,
        border: color.primary_intense,
    },
    tag: {
        background: color.accent_2,
        text: color.primary_dark,
    },
    banner: {
        default: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.primary_dark,
            border: color.primary_dark,
            linkText: color.primary_dark,
            linkBackground: color.primary_dark,
            linkHoverText: color.accent_2,
            linkHoverBackground: `#fdf3db`,
        },
        accent: {
            text: color.bg_light,
            background: `${color.secondary}15`,
            icon: color.secondary,
            border: color.secondary,
            linkText: color.secondary,
            linkBackground: `${color.secondary}15`,
            linkHoverText: color.secondary,
            linkHoverBackground: `${color.secondary}15`,
        },
        error: {
            text: color.bg_light,
            background: color.bg_light,
            icon: color.error,
            border: color.error,
            linkText: color.error,
            linkBackground: `${color.error}15`,
            linkHoverText: color.error,
            linkHoverBackground: `${color.error}15`,
        },
        warning: {
            text: color.primary_dark,
            background: `${color.accent_2}15`,
            icon: color.primary_dark,
            border: color.accent_2,
            linkText: color.primary_dark,
            linkBackground: color.accent_2,
            linkHoverText: color.accent_2,
            linkHoverBackground: `${color.accent_2}15`,
        },
        success: {
            text: color.bg_light,
            background: `${color.success}15`,
            icon: color.success,
            border: color.success,
            linkText: color.success,
            linkBackground: `${color.success}15`,
            linkHoverText: color.success,
            linkHoverBackground: `${color.success}15`,
        },
        info: {
            text: color.primary_dark,
            background: `${color.accent}15`,
            icon: color.accent,
            border: color.accent,
            linkText: color.primary_dark,
            linkBackground: `${color.neutral_accent_1}15`,
            linkHoverText: color.neutral_accent_1,
            linkHoverBackground: `${color.neutral_accent_1}15`,
        },
    },
    button: {
        primary: {
            text: color.bg_light,
            background: color.primary_dark,
            backgroundImage: true,
            icon: color.tertiary,
            border: color.secondary_light,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
        secondary: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.tertiary,
            border: color.secondary,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
        accent: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.secondary,
            border: color.secondary,
            hoverText: color.secondary,
            hoverBackground: color.neutral_accent_2,
        },
        simple: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.primary_dark,
            border: 'none',
            hoverText: color.primary_dark,
            hoverBackground: color.bg_light,
        },
        quick: {
            text: color.neutral,
            background: color.bg_light,
            icon: color.tertiary,
            border: color.neutral,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
    },
};

export const emotionDarkTheme: Theme = {
    mode: 'dark',
    panelPosition: 'right',
    panelOrientation: 'vertical',
    color: {
        background: color.bg_dark,
        card: color.primary_intense,
        cardHover: color.primary_light,
        accentCard: color.primary_dark,
        border: color.primary_light,
        text: color.bg_light,
        textBody: color.border,
        error: color.error_light,
        neutral: color.border,
        link: color.accent_2,
        accent: color.accent,
        tint: color.accent,
        danger: color.tertiary_updated,
        success: color.success_light,
        secondary: color.secondary_light,
        sidebarDivider: color.secondary_light + '20',
        indicator: color.accent,
        waveVoice: color.accent,
        waveDictation: color.accent_2,
    },

    input: {
        text: color.bg_light, // onSurface
        placeholder: color.primary_light, // surfaceVariant
        border: color.neutral_dark, // outlineVariant
        background: color.neutral_dark, // onSurfaceVariant
    },
    dialog: {
        background: color.bg_light,
        text: color.primary_intense,
        warn: color.accent_2,
        success: color.success_light,
        remote: color.accent,
        border: color.primary_intense,
    },
    tag: {
        background: color.secondary_light,
        text: color.primary_dark,
    },
    banner: {
        default: {
            text: color.bg_light,
            background: color.primary_dark,
            icon: color.bg_light,
            border: color.primary_dark,
            linkText: color.primary_dark,
            linkBackground: color.primary_dark,
            linkHoverText: color.accent_2,
            linkHoverBackground: `#fdf3db`,
        },
        accent: {
            text: color.bg_light,
            background: `${color.secondary}15`,
            icon: color.bg_light,
            border: color.secondary,
            linkText: color.bg_light,
            linkBackground: `${color.secondary}15`,
            linkHoverText: color.bg_light,
            linkHoverBackground: `${color.secondary}15`,
        },
        error: {
            text: color.bg_light,
            background: color.bg_dark,
            icon: color.tertiary,
            border: color.tertiary,
            linkText: color.tertiary,
            linkBackground: color.bg_dark,
            linkHoverText: color.tertiary,
            linkHoverBackground: color.bg_dark,
            buttonText: color.bg_dark,
        },
        warning: {
            text: color.bg_light,
            background: `${color.accent_2}25`,
            icon: color.bg_light,
            border: color.accent_2,
            linkText: color.accent_2,
            linkBackground: color.accent_2,
            linkHoverText: color.bg_light,
            linkHoverBackground: `${color.accent_2}15`,
            buttonText: color.primary_dark,
        },
        success: {
            text: color.bg_light,
            background: `${color.success}15`,
            icon: color.success,
            border: color.success,
            linkText: color.bg_light,
            linkBackground: `${color.success}15`,
            linkHoverText: color.success,
            linkHoverBackground: `${color.success}15`,
        },
        info: {
            text: color.primary_dark,
            background: `${color.accent}15`,
            icon: color.accent,
            border: color.accent,
            linkText: color.bg_light,
            linkBackground: `${color.neutral_accent_1}15`,
            linkHoverText: color.neutral_accent_1,
            linkHoverBackground: `${color.neutral_accent_1}15`,
        },
    },
    button: {
        primary: {
            text: color.primary_dark,
            background: color.accent_2,
            icon: color.accent_2,
            border: color.accent_2,
            hoverText: color.accent_2,
            hoverBackground: 'transparent',
        },
        secondary: {
            text: color.bg_light,
            background: color.primary_dark,
            icon: color.primary_dark,
            border: color.accent,
            hoverText: color.primary_dark,
            hoverBackground: color.accent,
        },
        accent: {
            text: color.border,
            background: color.bg_dark,
            icon: color.accent_2,
            border: color.accent_2,
            hoverText: color.accent_2,
            hoverBackground: color.primary_intense,
        },
        simple: {
            text: color.accent_2,
            background: 'transparent',
            icon: color.accent_2,
            border: 'none',
            hoverText: color.accent_2,
            hoverBackground: color.bg_dark,
        },
        quick: {
            text: color.bg_light,
            background: color.bg_dark,
            icon: color.tertiary,
            border: color.secondary_light,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
    },
};
